/**
 * Iter208.78 — Public "Villa Reale" Site (Agenzia Immobiliare, editorial serif con sidebar fissa).
 *
 * Struttura: sidebar bordeaux fissa a sinistra (desktop) / mobile-bar in cima (mobile).
 * Pagine:
 *   - /site/:slug                        → Home (hero + search-box + listing-rows + story timeline + stats + testimonial + cta)
 *   - /site/:slug/annunci                → Griglia completa listing-row con filtri
 *   - /site/:slug/annunci/:pid           → Dettaglio annuncio + form contatto
 *   - /site/:slug/chi-siamo              → Timeline estesa + team
 *   - /site/:slug/contatti               → Form + recapiti + mappa
 *
 * Il palette-widget originale è rimosso.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteLocation, useSiteNavigate, SitePreviewLink as Link } from "@/lib/previewNav";
import { Editable, Removable } from "@/lib/siteEditing";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VR_CSS = `
.villa-reale-site {
  --bordeaux:#5C1A2A;--bordeaux-dark:#3E1019;--cream:#FBF6EE;--parchment:#F1E8D8;
  --brass:#C9A15A;--brass-dark:#A8823F;--charcoal:#2B2622;--muted:#7A6F63;--line:#E2D6C0;
  margin:0;background:var(--cream);color:var(--charcoal);
  font-family:'Jost', -apple-system, sans-serif;
  -webkit-font-smoothing:antialiased;min-height:100vh;
}
.villa-reale-site *{box-sizing:border-box;}
.villa-reale-site h1,.villa-reale-site h2,.villa-reale-site h3{font-family:'Cormorant Garamond', Georgia, serif;font-weight:500;color:var(--charcoal);text-wrap:balance;margin:0;}
.villa-reale-site .eyebrow{font-family:'Jost';font-size:0.7rem;letter-spacing:0.22em;text-transform:uppercase;color:var(--brass-dark);}
.villa-reale-site a{color:inherit;}
.villa-reale-site img{max-width:100%;display:block;}
.villa-reale-site .wrap{max-width:980px;margin:0 auto;padding:0 48px;}
.villa-reale-site .sidebar{position:fixed;top:0;left:0;bottom:0;width:266px;background:var(--bordeaux);color:var(--parchment);display:flex;flex-direction:column;padding:44px 34px;z-index:80;}
.villa-reale-site .side-logo{font-family:'Cormorant Garamond';font-size:1.7rem;color:var(--cream);letter-spacing:0.03em;text-decoration:none;}
.villa-reale-site .side-logo em{display:block;font-style:normal;font-size:0.62rem;letter-spacing:0.24em;text-transform:uppercase;color:var(--brass);margin-top:6px;}
.villa-reale-site .side-nav{list-style:none;margin:64px 0 0;padding:0;display:flex;flex-direction:column;gap:4px;}
.villa-reale-site .side-nav a{text-decoration:none;color:var(--parchment);font-family:'Cormorant Garamond';font-size:1.32rem;padding:11px 0;border-bottom:1px solid rgba(201,161,90,0.18);display:block;transition:color .2s ease,padding-left .2s ease;}
.villa-reale-site .side-nav a:hover,.villa-reale-site .side-nav a.active{color:var(--brass);padding-left:6px;}
.villa-reale-site .side-spacer{flex:1;}
.villa-reale-site .side-contact{font-size:0.86rem;line-height:1.9;color:#D8C6AE;border-top:1px solid rgba(201,161,90,0.18);padding-top:22px;}
.villa-reale-site .side-cta{margin-top:22px;display:inline-block;text-align:center;border:1px solid var(--brass);color:var(--brass);text-decoration:none;padding:12px 0;font-size:0.78rem;letter-spacing:0.1em;text-transform:uppercase;transition:background .2s ease,color .2s ease;}
.villa-reale-site .side-cta:hover{background:var(--brass);color:var(--bordeaux-dark);}
.villa-reale-site .side-social{display:flex;gap:16px;margin-top:26px;font-size:0.76rem;letter-spacing:0.05em;color:#B99C7C;}
.villa-reale-site .mobile-bar{display:none;position:sticky;top:0;z-index:90;background:var(--bordeaux);color:var(--cream);align-items:center;justify-content:space-between;padding:16px 24px;}
.villa-reale-site .mobile-bar .side-logo{font-size:1.3rem;}
.villa-reale-site .mobile-toggle{background:none;border:1px solid var(--brass);color:var(--brass);padding:8px 14px;font-family:'Jost';font-size:0.75rem;letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;}
.villa-reale-site .mobile-panel{display:none;background:var(--bordeaux-dark);padding:10px 24px 26px;}
.villa-reale-site .mobile-panel.open{display:block;}
.villa-reale-site .mobile-panel a{display:block;color:var(--parchment);text-decoration:none;font-family:'Cormorant Garamond';font-size:1.3rem;padding:10px 0;border-bottom:1px solid rgba(201,161,90,0.15);}
.villa-reale-site .main{margin-left:266px;}
.villa-reale-site .hero{position:relative;background:var(--bordeaux-dark);color:var(--cream);overflow:hidden;min-height:560px;display:flex;align-items:center;justify-content:center;text-align:center;padding:0;}
.villa-reale-site .hero-art{position:absolute;inset:0;opacity:0.5;}
.villa-reale-site .hero-art svg{width:100%;height:100%;}
.villa-reale-site .hero-content{position:relative;z-index:2;max-width:640px;padding:80px 32px;}
.villa-reale-site .hero-content .eyebrow{color:var(--brass);}
.villa-reale-site .hero-content h1{font-size:clamp(2.6rem,4.6vw,4rem);line-height:1.08;margin:18px 0 22px;color:var(--cream);}
.villa-reale-site .hero-content p{font-size:1.08rem;line-height:1.7;color:#E8D9C3;max-width:480px;margin:0 auto 34px;}
.villa-reale-site .btn{display:inline-flex;align-items:center;gap:10px;background:var(--brass);color:var(--bordeaux-dark);padding:14px 30px;font-family:'Jost';font-size:0.82rem;letter-spacing:0.1em;text-transform:uppercase;text-decoration:none;border:1px solid var(--brass);transition:background .2s ease,color .2s ease;cursor:pointer;}
.villa-reale-site .btn:hover{background:transparent;color:var(--brass);}
.villa-reale-site .btn-outline{background:transparent;color:var(--cream);border-color:rgba(251,246,238,0.5);}
.villa-reale-site .btn-outline:hover{background:rgba(251,246,238,0.08);color:var(--cream);border-color:var(--cream);}
.villa-reale-site .search-frame{margin:-46px auto 0;position:relative;z-index:5;max-width:880px;padding:0 24px;}
.villa-reale-site .search-box{background:var(--cream);border:1px solid var(--brass);padding:28px 34px;display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:26px;align-items:end;box-shadow:0 24px 50px -28px rgba(43,38,34,0.35);}
.villa-reale-site .search-box .field label{display:block;font-size:0.68rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--brass-dark);margin-bottom:8px;}
.villa-reale-site .search-box select,.villa-reale-site .search-box input{width:100%;background:none;border:none;border-bottom:1px solid var(--line);padding:6px 0;font-family:'Jost';font-size:0.96rem;color:var(--charcoal);outline:none;}
.villa-reale-site section{padding:110px 0;}
.villa-reale-site .sec-head{text-align:center;max-width:560px;margin:0 auto 60px;}
.villa-reale-site .sec-head h2{font-size:clamp(1.9rem,3vw,2.6rem);margin-top:12px;}
.villa-reale-site .listing-row{display:grid;grid-template-columns:1.1fr 1fr;gap:56px;align-items:center;padding:52px 0;border-bottom:1px solid var(--line);}
.villa-reale-site .listing-row:first-of-type{border-top:1px solid var(--line);}
.villa-reale-site .listing-row:nth-of-type(even){direction:rtl;}
.villa-reale-site .listing-row:nth-of-type(even) > *{direction:ltr;}
.villa-reale-site .listing-media{aspect-ratio:5/4;overflow:hidden;position:relative;background:var(--parchment);}
.villa-reale-site .listing-media svg,.villa-reale-site .listing-media img{width:100%;height:100%;object-fit:cover;}
.villa-reale-site .listing-tag{position:absolute;top:16px;left:16px;background:var(--bordeaux);color:var(--cream);font-size:0.66rem;letter-spacing:0.1em;text-transform:uppercase;padding:6px 14px;}
.villa-reale-site .listing-price{font-family:'Cormorant Garamond';font-size:1.7rem;color:var(--bordeaux);margin-bottom:6px;}
.villa-reale-site .listing-title{font-size:1.7rem;margin-bottom:8px;}
.villa-reale-site .listing-loc{font-size:0.9rem;color:var(--muted);margin-bottom:20px;}
.villa-reale-site .listing-meta{display:flex;gap:22px;margin-bottom:22px;}
.villa-reale-site .listing-meta span{font-size:0.85rem;color:var(--charcoal);display:flex;align-items:center;gap:7px;}
.villa-reale-site .listing-meta svg{width:15px;height:15px;stroke:var(--brass-dark);fill:none;}
.villa-reale-site .listing-link{font-size:0.8rem;letter-spacing:0.08em;text-transform:uppercase;text-decoration:none;color:var(--bordeaux);border-bottom:1px solid var(--bordeaux);padding-bottom:2px;}
.villa-reale-site .story{background:var(--parchment);}
.villa-reale-site .timeline{display:grid;grid-template-columns:repeat(4,1fr);gap:0;position:relative;margin-top:20px;}
.villa-reale-site .timeline::before{content:"";position:absolute;top:10px;left:0;right:0;height:1px;background:var(--brass);}
.villa-reale-site .t-item{padding-top:36px;position:relative;text-align:center;padding-left:16px;padding-right:16px;}
.villa-reale-site .t-item::before{content:"";position:absolute;top:5px;left:50%;transform:translateX(-50%);width:11px;height:11px;border-radius:50%;background:var(--bordeaux);border:2px solid var(--brass);}
.villa-reale-site .t-item .yr{font-family:'Cormorant Garamond';font-size:1.6rem;color:var(--bordeaux);}
.villa-reale-site .t-item p{font-size:0.88rem;color:var(--muted);margin:8px 0 0;line-height:1.6;}
.villa-reale-site .stats-band{border-top:1px solid var(--brass);border-bottom:1px solid var(--brass);padding:64px 0;}
.villa-reale-site .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);text-align:center;}
.villa-reale-site .stat .num{font-family:'Cormorant Garamond';font-size:clamp(2rem,3.2vw,2.7rem);color:var(--bordeaux);}
.villa-reale-site .stat .lbl{font-size:0.72rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted);margin-top:8px;}
.villa-reale-site .testimonial{text-align:center;}
.villa-reale-site .testimonial blockquote{font-family:'Cormorant Garamond';font-style:italic;font-size:clamp(1.5rem,2.4vw,2.1rem);line-height:1.5;max-width:760px;margin:0 auto;color:var(--charcoal);}
.villa-reale-site .testimonial cite{display:block;margin-top:26px;font-size:0.78rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--brass-dark);font-style:normal;}
.villa-reale-site .cta{background:var(--bordeaux);color:var(--cream);text-align:center;}
.villa-reale-site .cta h2{color:var(--cream);margin-bottom:16px;}
.villa-reale-site .cta p{color:#E8D9C3;max-width:480px;margin:0 auto 30px;}
.villa-reale-site footer{padding:40px 0;border-top:1px solid var(--line);}
.villa-reale-site .foot-row{display:flex;justify-content:space-between;align-items:center;font-size:0.8rem;color:var(--muted);flex-wrap:wrap;gap:12px;padding:0 48px;max-width:980px;margin:0 auto;}
/* Pagine aggiuntive */
.villa-reale-site .contact-page,.villa-reale-site .listing-detail-page,.villa-reale-site .about-full,.villa-reale-site .annunci-full{padding:80px 0 100px;}
.villa-reale-site .contact-grid{display:grid;grid-template-columns:1.1fr 0.9fr;gap:56px;align-items:flex-start;max-width:980px;margin:0 auto;padding:0 48px;}
.villa-reale-site .field-row{margin-bottom:18px;}
.villa-reale-site .field-row label{display:block;font-size:0.7rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--brass-dark);margin-bottom:8px;}
.villa-reale-site .field-row input,.villa-reale-site .field-row textarea{width:100%;background:var(--cream);border:1px solid var(--line);padding:12px 14px;font-family:'Jost';font-size:0.95rem;color:var(--charcoal);outline:none;transition:border-color .2s;}
.villa-reale-site .field-row input:focus,.villa-reale-site .field-row textarea:focus{border-color:var(--brass);}
.villa-reale-site .field-row textarea{min-height:130px;resize:vertical;}
.villa-reale-site .contact-side{background:var(--parchment);border:1px solid var(--brass);padding:32px 28px;}
.villa-reale-site .contact-side h3{margin-bottom:14px;font-size:1.4rem;font-family:'Cormorant Garamond';color:var(--bordeaux);}
.villa-reale-site .contact-side .row{display:flex;gap:10px;align-items:flex-start;margin:14px 0;font-size:0.92rem;color:var(--charcoal);}
.villa-reale-site .contact-side .row svg{width:16px;height:16px;stroke:var(--brass-dark);fill:none;flex-shrink:0;margin-top:2px;}
.villa-reale-site .contact-map{margin-top:24px;aspect-ratio:16/10;background:var(--cream);border:1px solid var(--line);overflow:hidden;}
.villa-reale-site .contact-map iframe{width:100%;height:100%;border:none;}
.villa-reale-site .filters-bar{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:32px;padding:20px 24px;background:var(--parchment);border:1px solid var(--brass);}
.villa-reale-site .filters-bar select,.villa-reale-site .filters-bar input{background:var(--cream);border:1px solid var(--line);color:var(--charcoal);padding:10px 12px;font-family:'Jost';font-size:0.9rem;outline:none;}
.villa-reale-site .listing-hero{aspect-ratio:16/9;background:var(--parchment);border:1px solid var(--line);overflow:hidden;margin-bottom:36px;}
.villa-reale-site .listing-hero svg,.villa-reale-site .listing-hero img{width:100%;height:100%;object-fit:cover;}
.villa-reale-site .listing-detail-grid{display:grid;grid-template-columns:1.4fr 1fr;gap:48px;max-width:980px;margin:0 auto;padding:0 48px;}
.villa-reale-site .listing-info-panel{background:var(--parchment);border:1px solid var(--brass);padding:28px;position:sticky;top:32px;}
.villa-reale-site .listing-info-panel .price{font-family:'Cormorant Garamond';font-size:2rem;color:var(--bordeaux);margin-bottom:8px;}
.villa-reale-site .listing-info-panel .stats-list{display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;margin:20px 0 24px;padding:16px 0;border-top:1px solid var(--brass);border-bottom:1px solid var(--brass);font-size:0.85rem;color:var(--charcoal);}
.villa-reale-site .listing-info-panel .stats-list .k{font-size:0.66rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted);display:block;margin-bottom:4px;}
.villa-reale-site .back-link{display:inline-flex;align-items:center;gap:8px;font-size:0.8rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--bordeaux);text-decoration:none;margin-bottom:20px;border-bottom:1px solid var(--bordeaux);padding-bottom:2px;}
.villa-reale-site .empty-state{padding:80px 20px;text-align:center;color:var(--muted);font-size:0.9rem;}
.villa-reale-site .team-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px;margin-top:44px;}
.villa-reale-site .team-card{background:var(--parchment);border:1px solid var(--line);padding:24px;text-align:center;}
.villa-reale-site .team-card .avatar{aspect-ratio:1;background:var(--bordeaux);margin-bottom:16px;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond';font-size:2.6rem;color:var(--brass);}
.villa-reale-site .team-card h4{font-family:'Cormorant Garamond';font-size:1.3rem;color:var(--charcoal);margin:0 0 4px;font-weight:500;}
.villa-reale-site .team-card .role{font-size:0.72rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--brass-dark);}
.villa-reale-site .annunci-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:48px;max-width:1180px;margin:0 auto;padding:0 48px;}
@media (max-width: 980px){
  .villa-reale-site .sidebar{display:none;}
  .villa-reale-site .main{margin-left:0;}
  .villa-reale-site .mobile-bar{display:flex;}
  .villa-reale-site .wrap{padding:0 24px;}
  .villa-reale-site .search-box{grid-template-columns:1fr 1fr;row-gap:16px;}
  .villa-reale-site .search-box .btn{grid-column:1/-1;justify-content:center;}
  .villa-reale-site .listing-row{grid-template-columns:1fr;direction:ltr !important;}
  .villa-reale-site .timeline{grid-template-columns:1fr 1fr;row-gap:36px;}
  .villa-reale-site .timeline::before{display:none;}
  .villa-reale-site .stats-grid,.villa-reale-site .team-grid,.villa-reale-site .annunci-grid{grid-template-columns:1fr;row-gap:28px;}
  .villa-reale-site .contact-grid,.villa-reale-site .listing-detail-grid{grid-template-columns:1fr;gap:36px;padding:0 24px;}
  .villa-reale-site .listing-info-panel{position:static;}
  .villa-reale-site .foot-row{padding:0 24px;}
}
`;

function HeroArt() {
  return (
    <svg viewBox="0 0 1200 560" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="1200" height="560" fill="#3E1019"/>
      <g stroke="#C9A15A" strokeWidth="1" fill="none" opacity="0.5">
        <path d="M150 460 L150 260 L400 140 L650 260 L650 460 Z"/>
        <path d="M700 460 L700 300 L900 200 L1100 300 L1100 460 Z"/>
        <path d="M150 460 L1100 460"/>
        <circle cx="400" cy="140" r="4" fill="#C9A15A"/>
      </g>
    </svg>
  );
}

function ListingArt({ variant = 0 }) {
  const strokes = ["#5C1A2A", "#C9A15A", "#5C1A2A"];
  return (
    <svg viewBox="0 0 400 320" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="400" height="320" fill="#F1E8D8"/>
      <g stroke={strokes[variant % 3]} strokeWidth="1.2" fill="none">
        <path d="M60 260 L60 140 L200 70 L340 140 L340 260 Z"/>
        <rect x="100" y="180" width="50" height="80"/>
        <rect x="180" y="170" width="60" height="90"/>
        <rect x="270" y="180" width="50" height="80"/>
      </g>
    </svg>
  );
}

function Sidebar({ slug, studio, activePage }) {
  const base = `/site/${slug}`;
  const nav = [["", "Home", "home"], ["/annunci", "Annunci", "annunci"], ["/chi-siamo", "Chi siamo", "chi-siamo"], ["/contatti", "Contatti", "contatti"]];
  const [insegna, ...rest] = (studio.name || "Villa Reale").split(" ");
  const subtitle = rest.length ? rest.join(" ") : "Immobiliare";
  return (
    <aside className="sidebar" data-testid="vr-sidebar">
      <Link to={base} className="side-logo" data-testid="vr-side-logo">
        {studio.name || "Villa Reale"}<em>{subtitle}</em>
      </Link>
      <ul className="side-nav">
        {nav.map(([p, label, key]) => (
          <li key={key}><Link to={`${base}${p}`} className={activePage === key ? "active" : ""} data-testid={`vr-nav-${key}`}>{label}</Link></li>
        ))}
      </ul>
      <div className="side-spacer" />
      <div className="side-contact" data-testid="vr-side-contact">
        {studio.address && <>{studio.address}<br/></>}
        {studio.phone && <>{studio.phone}<br/></>}
        {studio.email && <>{studio.email}</>}
      </div>
      <Link to={`${base}/contatti`} className="side-cta" data-testid="vr-side-cta">Richiedi valutazione</Link>
      <div className="side-social"><span>Instagram</span><span>LinkedIn</span></div>
    </aside>
  );
}

function MobileBar({ slug, studio }) {
  const [open, setOpen] = useState(false);
  const base = `/site/${slug}`;
  const location = useSiteLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  return (
    <>
      <div className="mobile-bar" data-testid="vr-mobile-bar">
        <div className="side-logo">{studio.name || "Villa Reale"}</div>
        <button className="mobile-toggle" onClick={() => setOpen(!open)} data-testid="vr-mobile-toggle">Menu</button>
      </div>
      <div className={"mobile-panel" + (open ? " open" : "")} data-testid="vr-mobile-panel">
        <Link to={base}>Home</Link>
        <Link to={`${base}/annunci`}>Annunci</Link>
        <Link to={`${base}/chi-siamo`}>Chi siamo</Link>
        <Link to={`${base}/contatti`}>Contatti</Link>
      </div>
    </>
  );
}

function ListingRow({ p, slug, index = 0 }) {
  const priceFmt = p.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <div className="listing-row" data-testid={`vr-listing-row-${p.id}`}>
      <div className="listing-media">
        <span className="listing-tag">{p.tag || "In vendita"}</span>
        {p.cover_url ? <img src={p.cover_url} alt={p.address}/> : <ListingArt variant={index}/>}
      </div>
      <div>
        <div className="listing-price">{priceFmt}</div>
        <h3 className="listing-title">{p.address || "Immobile di pregio"}</h3>
        <div className="listing-loc">{p.city}</div>
        <div className="listing-meta">
          {p.surface_m2 ? <span><svg viewBox="0 0 24 24" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="1"/></svg>{p.surface_m2} mq</span> : null}
          {p.rooms ? <span><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 20h16M6 20V10l6-4 6 4v10"/></svg>{p.rooms} locali</span> : null}
          {p.bathrooms ? <span><svg viewBox="0 0 24 24" strokeWidth="1.6"><circle cx="12" cy="12" r="9"/></svg>{p.bathrooms} bagni</span> : null}
        </div>
        <Link to={`/site/${slug}/annunci/${p.id}`} className="listing-link" data-testid={`vr-listing-link-${p.id}`}>Scopri l'immobile</Link>
      </div>
    </div>
  );
}

function ContactFormVR({ slug, propertyId, testIdPrefix = "vr-contact" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nome, email e messaggio sono obbligatori."); return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/site/${slug}/contact`, { ...form, property_id: propertyId || null });
      setSent(true); setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Richiesta ricevuta — ti risponderemo entro 48 ore.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito.");
    } finally { setSending(false); }
  };
  if (sent) return (
    <div className="contact-side" data-testid={`${testIdPrefix}-success`} style={{background:"var(--bordeaux)",color:"var(--cream)",borderColor:"var(--bordeaux)"}}>
      <h3 style={{color:"var(--cream)"}}>Richiesta ricevuta.</h3>
      <p style={{fontSize:"0.95rem",color:"#E8D9C3",lineHeight:1.6}}>Un consulente ti contatterà entro 48 ore con la discrezione dovuta.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`}>
      <div className="field-row"><label>Nome e cognome</label><input value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))} data-testid={`${testIdPrefix}-name`} required/></div>
      <div className="field-row"><label>Email</label><input type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} data-testid={`${testIdPrefix}-email`} required/></div>
      <div className="field-row"><label>Telefono</label><input value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value}))} data-testid={`${testIdPrefix}-phone`}/></div>
      <div className="field-row"><label>Messaggio</label><textarea value={form.message} onChange={(e)=>setForm(f=>({...f, message:e.target.value}))} data-testid={`${testIdPrefix}-message`} required/></div>
      <button type="submit" className="btn" disabled={sending} data-testid={`${testIdPrefix}-submit`}>{sending ? "Invio…" : "Invia richiesta"}</button>
    </form>
  );
}

function VRHome({ slug, studio, properties }) {
  const founded = studio.founded_year;
  const anni = founded ? (2026 - Number(founded)) : "—";
  return (
    <>
      <Removable sectionKey="hero">
      <section className="hero" data-testid="vr-section-hero" style={{padding:0}}>
        <div className="hero-art"><HeroArt/></div>
        <div className="hero-content">
          <span className="eyebrow">Agenzia immobiliare{founded ? ` dal ${founded}` : ""}</span>
          <Editable field="vr.hero.h1" as="h1">Proprietà che meritano una storia</Editable>
          <Editable field="vr.hero.p" as="p" multiline>Un patrimonio selezionato di case ed immobili di pregio, seguito con la discrezione e la cura che meritano.</Editable>
          <div style={{display:"flex",gap:"16px",justifyContent:"center",flexWrap:"wrap"}}>
            <Link to={`/site/${slug}/annunci`} className="btn" data-testid="vr-hero-cta-annunci"><Editable field="vr.hero.cta1">Sfoglia gli annunci</Editable></Link>
            <Link to={`/site/${slug}/contatti`} className="btn btn-outline" data-testid="vr-hero-cta-vendi"><Editable field="vr.hero.cta2">Vendi con noi</Editable></Link>
          </div>
        </div>
      </section>
      </Removable>

      <div className="search-frame">
        <div className="search-box" data-testid="vr-search-box">
          <div className="field"><label>Località</label><input placeholder={studio.city || "Bologna"} defaultValue={studio.city || ""}/></div>
          <div className="field"><label>Tipologia</label><select defaultValue=""><option value="">Tutte</option><option>Villa</option><option>Attico</option><option>Dimora storica</option></select></div>
          <div className="field"><label>Fascia di prezzo</label><select defaultValue=""><option value="">Tutte</option><option>500k – 800k</option><option>800k – 1,2M</option><option>1,2M+</option></select></div>
          <Link to={`/site/${slug}/annunci`} className="btn" data-testid="vr-search-btn">Cerca</Link>
        </div>
      </div>

      {properties?.length > 0 && (
        <Removable sectionKey="listings">
        <section className="listings" data-testid="vr-section-listings">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">Selezione attuale</span>
              <h2>Immobili di pregio</h2>
            </div>
            {properties.slice(0, 3).map((p, i) => <ListingRow key={p.id} p={p} slug={slug} index={i}/>)}
          </div>
        </section>
        </Removable>
      )}

      <Removable sectionKey="story">
      <section className="story" data-testid="vr-section-story">
        <div className="wrap">
          <div className="sec-head">
            <span className="eyebrow">{founded ? `Dal ${founded}` : "La nostra storia"}</span>
            <Editable field="vr.story.h2" as="h2">La nostra storia</Editable>
          </div>
          <div className="timeline">
            <div className="t-item" data-testid="vr-t-item-0"><div className="yr">{founded || "1998"}</div><Editable field="vr.story.p0" as="p" multiline>{`Fondazione dell'agenzia${studio.city ? ` a ${studio.city}` : ""}.`}</Editable></div>
            <div className="t-item" data-testid="vr-t-item-1"><div className="yr">{founded ? Number(founded) + 9 : "2007"}</div><Editable field="vr.story.p1" as="p" multiline>Apertura della divisione immobili di pregio.</Editable></div>
            <div className="t-item" data-testid="vr-t-item-2"><div className="yr">{founded ? Number(founded) + 17 : "2015"}</div><Editable field="vr.story.p2" as="p" multiline>Superate le 300 compravendite seguite personalmente.</Editable></div>
            <div className="t-item" data-testid="vr-t-item-3"><div className="yr">2026</div><Editable field="vr.story.p3" as="p" multiline>Un team di consulenti dedicati, radicati nel territorio.</Editable></div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="stats">
      <section className="stats-band" data-testid="vr-section-stats" style={{paddingTop:0,paddingBottom:0}}>
        <div className="wrap">
          <div className="stats-grid">
            <div className="stat"><div className="num">412</div><div className="lbl">Immobili venduti</div></div>
            <div className="stat"><div className="num">{anni}</div><div className="lbl">Anni di attività</div></div>
            <div className="stat"><div className="num">9</div><div className="lbl">Consulenti dedicati</div></div>
            <div className="stat"><div className="num">1,2M€</div><div className="lbl">Valore medio vendite</div></div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="testimonial">
      <section className="testimonial" data-testid="vr-section-testimonial">
        <div className="wrap">
          <Editable field="vr.testimonial.quote" as="blockquote" multiline>Non vendono semplicemente case: custodiscono il valore di ciò che rappresentano per chi le abita.</Editable>
          <Editable field="vr.testimonial.cite" as="cite">— Famiglia Bertolini, proprietà venduta nel 2025</Editable>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="cta">
      <section className="cta" data-testid="vr-section-cta">
        <div className="wrap">
          <Editable field="vr.cta.h2" as="h2">Desidera una valutazione riservata?</Editable>
          <Editable field="vr.cta.p" as="p" multiline>I nostri consulenti effettuano perizie discrete, su appuntamento, per proprietari che cercano privacy e competenza.</Editable>
          <Link to={`/site/${slug}/contatti`} className="btn" data-testid="vr-cta-btn"><Editable field="vr.cta.btn">Richiedi un appuntamento</Editable></Link>
        </div>
      </section>
      </Removable>
    </>
  );
}

function VRAnnunci({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/site/${slug}/properties`).then((r) => setItems(r.data.properties || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [slug]);
  const cities = useMemo(() => Array.from(new Set(items.map(i => i.city).filter(Boolean))), [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (city && p.city !== city) return false;
    if (search && !(p.address || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, city, search]);
  return (
    <section className="annunci-full" data-testid="vr-page-annunci">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Il nostro catalogo</span>
          <h2>Tutti gli immobili di pregio</h2>
        </div>
      </div>
      <div className="wrap">
        <div className="filters-bar" data-testid="vr-annunci-filters">
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="vr-filter-city">
            <option value="">Tutte le località</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Cerca per indirizzo…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vr-filter-search" style={{flex:1, minWidth:"220px"}}/>
          <span style={{marginLeft:"auto",fontSize:"0.8rem",letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)"}} data-testid="vr-annunci-count">{filtered.length} immobil{filtered.length === 1 ? "e" : "i"}</span>
        </div>
        {loading ? <div className="empty-state">Caricamento…</div>
          : filtered.length === 0 ? <div className="empty-state" data-testid="vr-annunci-empty">Nessun immobile disponibile.</div>
          : filtered.map((p, i) => <ListingRow key={p.id} p={p} slug={slug} index={i}/>)}
      </div>
    </section>
  );
}

function VRChiSiamo({ slug, studio }) {
  const founded = studio.founded_year;
  const team = [
    { name: studio.name?.split(" ")[0] || "Villa Reale", role: "Fondatore", initial: (studio.name || "V")[0] },
    { name: "Alessandro Marchesi", role: "Consulente Senior", initial: "A" },
    { name: "Chiara Lombardi", role: "Valutazioni Pregio", initial: "C" },
  ];
  return (
    <section className="about-full" data-testid="vr-page-chi-siamo">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Chi siamo</span>
          <h2>La nostra storia</h2>
        </div>
        <p style={{fontSize:"1.1rem",lineHeight:1.8,maxWidth:"720px",margin:"0 auto",color:"var(--charcoal)",fontFamily:"'Cormorant Garamond'",fontStyle:"italic",textAlign:"center"}}>
          {studio.name || "Villa Reale"} nasce{founded ? ` nel ${founded}` : ""}{studio.city ? ` a ${studio.city}` : ""} con l'ambizione di custodire il valore degli immobili di pregio del territorio. Selezioniamo con cura, seguiamo con discrezione, concludiamo con competenza.
        </p>

        <div className="timeline" style={{marginTop:"64px"}}>
          <div className="t-item"><div className="yr">{founded || "1998"}</div><p>Fondazione dell'agenzia{studio.city ? ` nel centro di ${studio.city}` : ""}.</p></div>
          <div className="t-item"><div className="yr">{founded ? Number(founded)+9 : "2007"}</div><p>Apertura divisione ville storiche.</p></div>
          <div className="t-item"><div className="yr">{founded ? Number(founded)+17 : "2015"}</div><p>Oltre 300 compravendite seguite.</p></div>
          <div className="t-item"><div className="yr">2026</div><p>Team di 9 consulenti radicati sul territorio.</p></div>
        </div>

        <div style={{marginTop:"72px"}}>
          <div className="sec-head" style={{marginBottom:"24px"}}>
            <span className="eyebrow">Il team</span>
            <h2>Chi seguirà la sua pratica</h2>
          </div>
          <div className="team-grid">
            {team.map((m, i) => (
              <div className="team-card" key={i} data-testid={`vr-team-${i}`}>
                <div className="avatar">{m.initial}</div>
                <h4>{m.name}</h4>
                <div className="role">{m.role}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function VRContatti({ slug, studio }) {
  const mapQuery = encodeURIComponent(studio.address || studio.city || "Bologna");
  return (
    <section className="contact-page" data-testid="vr-page-contatti">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Contatti riservati</span>
          <h2>Restiamo in contatto</h2>
        </div>
      </div>
      <div className="contact-grid">
        <div><ContactFormVR slug={slug} testIdPrefix="vr-contatti"/></div>
        <aside className="contact-side" data-testid="vr-contatti-side">
          <h3>Recapiti</h3>
          {studio.phone && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>{studio.phone}</div>}
          {studio.email && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 4h16v16H4z"/><path d="M4 4l8 8 8-8"/></svg>{studio.email}</div>}
          {studio.address && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{studio.address}</div>}
          <div className="contact-map" data-testid="vr-contatti-map">
            <iframe title="Mappa" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy"/>
          </div>
        </aside>
      </div>
    </section>
  );
}

function VRListingDetail({ slug, pid }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/site/${slug}/properties/${pid}`)
      .then((r) => setProp(r.data.property)).catch(() => setProp(null)).finally(() => setLoading(false));
  }, [slug, pid]);
  if (loading) return <section className="listing-detail-page"><div className="wrap empty-state">Caricamento…</div></section>;
  if (!prop) return <section className="listing-detail-page"><div className="wrap empty-state" data-testid="vr-listing-detail-not-found">Immobile non disponibile.</div></section>;
  const priceFmt = prop.price ? `€ ${Number(prop.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <section className="listing-detail-page" data-testid="vr-page-listing-detail">
      <div className="wrap">
        <Link to={`/site/${slug}/annunci`} className="back-link" data-testid="vr-listing-back">← Torna agli annunci</Link>
        <div className="listing-hero">{prop.cover_url ? <img src={prop.cover_url} alt={prop.address}/> : <ListingArt variant={0}/>}</div>
      </div>
      <div className="listing-detail-grid">
        <div>
          <span className="eyebrow">{prop.tag || "In esclusiva"}</span>
          <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"14px",fontFamily:"'Cormorant Garamond'"}}>{prop.address}</h2>
          <p style={{color:"var(--muted)",fontSize:"1rem",marginTop:"6px"}}>{prop.city}</p>
          {prop.description && <p style={{fontSize:"1.05rem",lineHeight:1.75,marginTop:"32px",color:"var(--charcoal)"}}>{prop.description}</p>}
        </div>
        <aside className="listing-info-panel" data-testid="vr-listing-info-panel">
          <div className="price">{priceFmt}</div>
          <div className="stats-list">
            {prop.surface_m2 ? <div><span className="k">Superficie</span>{prop.surface_m2} mq</div> : null}
            {prop.rooms ? <div><span className="k">Locali</span>{prop.rooms}</div> : null}
            {prop.bathrooms ? <div><span className="k">Bagni</span>{prop.bathrooms}</div> : null}
            {prop.city ? <div><span className="k">Località</span>{prop.city}</div> : null}
          </div>
          <h3 style={{fontFamily:"'Cormorant Garamond'",fontSize:"1.3rem",color:"var(--bordeaux)",marginBottom:"18px"}}>Richiedi informazioni riservate</h3>
          <ContactFormVR slug={slug} propertyId={pid} testIdPrefix="vr-listing-contact"/>
        </aside>
      </div>
    </section>
  );
}

// ============================================================================
//  Root
// ============================================================================
export default function VillaRealeSite({ initialData, slug: slugProp }) {
  const location = useSiteLocation();
  const navigate = useSiteNavigate();
  const slug = slugProp;
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (initialData || !slug) return;
    axios.get(`${API}/public/site/${slug}`)
      .then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug, initialData]);

  const subPath = location.pathname.replace(`/site/${slug}`, "").replace(/^\//, "");
  const [seg1, seg2] = subPath.split("/");
  let activePage = "home";
  if (seg1 === "annunci") activePage = "annunci";
  else if (seg1 === "chi-siamo") activePage = "chi-siamo";
  else if (seg1 === "contatti") activePage = "contatti";

  if (loading) return <div className="villa-reale-site"><style>{VR_CSS}</style><div className="empty-state" style={{padding:"120px 20px"}}>Caricamento…</div></div>;
  if (notFound || !data) return (
    <div className="villa-reale-site" data-testid="vr-not-found"><style>{VR_CSS}</style>
      <div className="empty-state" style={{padding:"120px 20px"}}>Sito non trovato. <button onClick={() => navigate("/")} style={{background:"none",border:"none",color:"var(--bordeaux)",cursor:"pointer",textDecoration:"underline"}}>Torna alla homepage</button></div>
    </div>
  );

  const { studio, featured_properties: properties } = data;
  return (
    <div className="villa-reale-site" data-testid="vr-site-root" data-slug={slug}>
      <style>{VR_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,500&family=Jost:wght@400;500;600&display=swap" rel="stylesheet"/>
      <Sidebar slug={slug} studio={studio} activePage={activePage}/>
      <div className="main">
        <MobileBar slug={slug} studio={studio}/>
        {(!seg1) ? <VRHome slug={slug} studio={studio} properties={properties}/>
          : seg1 === "annunci" && !seg2 ? <VRAnnunci slug={slug}/>
          : seg1 === "annunci" && seg2 ? <VRListingDetail slug={slug} pid={seg2}/>
          : seg1 === "chi-siamo" ? <VRChiSiamo slug={slug} studio={studio}/>
          : seg1 === "contatti" ? <VRContatti slug={slug} studio={studio}/>
          : <div className="empty-state" style={{padding:"120px 20px"}}>Pagina non trovata.</div>}
        <footer data-testid="vr-footer">
          <div className="foot-row">
            <span>© {new Date().getFullYear()} {studio.name || "Villa Reale"}{studio.vat ? ` — P.IVA ${studio.vat}` : ""}</span>
            <span>{studio.address ? `${studio.address}${studio.phone ? " — " + studio.phone : ""}` : (studio.phone || "")}</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
